import { useEffect, useState } from 'react';
import type { RSOSGlobals } from '@rsos/utils/rsosGlobal';
import { Location } from '../types';

type MapProps = RSOSGlobals['map'];

function getMapCenter(map: MapProps): Location | undefined {
  if (!map) return undefined;

  if (map.mapProvider === 'esri') {
    const center = map.mapInstance.view?.center;
    if (center) {
      return { latitude: center.latitude, longitude: center.longitude };
    }
  } else if (map.mapProvider === 'google') {
    const center = map.mapInstance.getCenter();
    if (center) {
      return { latitude: center.lat(), longitude: center.lng() };
    }
  }
  return undefined;
}

const useLocationPicker = (map: MapProps) => {
  const [location, setLocation] = useState<Location | undefined>(() =>
    getMapCenter(map),
  );

  useEffect(() => {
    if (!map) {
      throw new Error('Map not initialized on window');
    }

    // If no location yet (e.g. map wasn't ready during init), try map center
    setLocation(prev => prev ?? getMapCenter(map));

    const handleESRIMapClick = (event: __esri.ViewClickEvent) => {
      setLocation({
        latitude: event.mapPoint.latitude,
        longitude: event.mapPoint.longitude,
      });
    };

    const handleGoogleMapClick = (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) {
        throw new Error('Could not get latlng from Google map click');
      }

      setLocation({
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng(),
      });
    };

    let listener:
      | google.maps.MapsEventListener
      | __esri.WatchHandle
      | undefined;

    if (map.mapProvider === 'esri') {
      listener = map.mapInstance.view?.on('click', handleESRIMapClick);
    } else if (map.mapProvider === 'google') {
      listener = map.mapInstance.addListener('click', handleGoogleMapClick);
    }

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [map]);

  return location;
};

export default useLocationPicker;
